import React, { useState, useEffect, useRef } from 'react';
import './CallRecorder.css';

interface CallRecorderProps {
  onSave: (callData: CallData) => void;
  onCancel: () => void;
  initialFrom?: string;
  initialTo?: string;
  clientPhoneNumber?: string; // Phone number to search for transcriptions
  userFirstname?: string; // Current user's first name for searching their calls
  userLastname?: string; // Current user's last name for searching their calls
  claimId?: string; // Claim ID to attach transcription to
}

export interface CallData {
  direction: 'incoming' | 'outgoing';
  subject: string;
  from: string;
  to: string;
  callDuration: number; // in seconds
  callAnswered: boolean;
  callNotes: string;
  units?: number; // Number of billable units (1 unit per 6 minutes)
}

interface GCSCall {
  blob_name: string;
  filename: string;
  user: {
    firstname: string;
    lastname: string;
    fullname: string;
  };
  extension: string;
  phone_number: string;
  call_datetime: string | null;
  call_id: string;
  file_size: number;
  created_at: string | null;
  transcription?: string | null; // Added after transcribing
}

export default function CallRecorder({
  onSave,
  onCancel,
  initialFrom = '',
  initialTo = '',
  clientPhoneNumber,
  userFirstname,
  userLastname,
  claimId
}: CallRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [direction, setDirection] = useState<'incoming' | 'outgoing'>('outgoing');
  const [subject, setSubject] = useState('');
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [callAnswered, setCallAnswered] = useState(true);
  const [callNotes, setCallNotes] = useState('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Transcription search state
  const [showTranscriptionSearch, setShowTranscriptionSearch] = useState(false);
  const [searchPhoneNumber, setSearchPhoneNumber] = useState(clientPhoneNumber || '');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<GCSCall[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedCall, setSelectedCall] = useState<GCSCall | null>(null);
  const [transcribing, setTranscribing] = useState(false);

  // Auto-start timer when component mounts
  useEffect(() => {
    const now = Date.now();
    setIsRecording(true);
    setStartTime(now);
    setElapsedTime(0);
  }, []);

  useEffect(() => {
    if (isRecording) {
      intervalRef.current = setInterval(() => {
        if (startTime) {
          setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
        }
      }, 100); // Update every 100ms for smooth display
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRecording, startTime]);

  const handleStart = () => {
    setIsRecording(true);
    setStartTime(Date.now());
    setElapsedTime(0);
  };

  const handleStop = () => {
    setIsRecording(false);
    if (startTime) {
      const finalDuration = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(finalDuration);
    }
  };

  const handleSave = () => {
    if (!subject.trim()) {
      alert('Please enter a subject for the call');
      return;
    }
    if (!from.trim() || !to.trim()) {
      alert('Please enter both caller and recipient information');
      return;
    }

    // Calculate units: 1 unit per 6 minutes, rounded up
    const durationInMinutes = elapsedTime / 60;
    const units = Math.ceil(durationInMinutes / 6);

    onSave({
      direction,
      subject: subject.trim(),
      from: from.trim(),
      to: to.trim(),
      callDuration: elapsedTime,
      callAnswered,
      callNotes: callNotes.trim(),
      units
    });
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDirectionChange = (newDirection: 'incoming' | 'outgoing') => {
    setDirection(newDirection);
    // Swap from/to when direction changes
    if (newDirection !== direction) {
      const temp = from;
      setFrom(to);
      setTo(temp);
    }
  };

  const searchTranscriptions = async () => {
    if (!searchPhoneNumber.trim() && (!userFirstname || !userLastname)) {
      setSearchError('Please enter a phone number or ensure user information is available');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const token = localStorage.getItem('token');

      // Build query params
      const params = new URLSearchParams();
      if (searchPhoneNumber.trim()) {
        params.append('phone_number', searchPhoneNumber);
      }
      if (userFirstname) {
        params.append('user_firstname', userFirstname);
      }
      if (userLastname) {
        params.append('user_lastname', userLastname);
      }
      params.append('days_back', '30');  // Search last 30 days
      params.append('limit', '50');

      const response = await fetch(
        `/api/calls/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.calls) {
        setSearchResults(data.calls);
        if (data.calls.length === 0) {
          setSearchError(`No calls found matching your criteria in the last 7 days`);
        }
      } else {
        throw new Error(data.error || 'Search failed');
      }
    } catch (error: any) {
      console.error('Error searching call recordings:', error);
      setSearchError(error.message || 'Failed to search for calls');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUseTranscription = async (call: GCSCall) => {
    setSelectedCall(call);
    setTranscribing(true);

    try {
      // If already transcribed, use existing transcription
      if (call.transcription) {
        setCallNotes(`TRANSCRIPTION:\n${call.transcription}`);
        setTranscribing(false);
        setShowTranscriptionSearch(false);
        return;
      }

      // Otherwise, transcribe the call
      const token = localStorage.getItem('token');
      const response = await fetch('/api/calls/transcribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          blob_name: call.blob_name
        })
      });

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.transcription) {
        // Update call object with transcription
        call.transcription = data.transcription;

        // Set call notes
        setCallNotes(`TRANSCRIPTION:\n${data.transcription}`);

        // Auto-fill subject if empty
        if (!subject.trim()) {
          const preview = data.transcription.substring(0, 100);
          setSubject(`Call with ${call.user.fullname} - ${preview}...`);
        }

        // Stop the timer and set the actual audio duration
        if (data.duration_seconds) {
          setIsRecording(false); // Stop the timer
          setElapsedTime(data.duration_seconds); // Set to actual audio duration
        }
      } else {
        throw new Error(data.error || 'Transcription failed');
      }
    } catch (error: any) {
      console.error('Error transcribing call:', error);
      alert(`Failed to transcribe call: ${error.message}`);
    } finally {
      setTranscribing(false);
      setShowTranscriptionSearch(false);
    }
  };

  return (
    <div className="call-recorder">
      <div className="call-recorder-header">
        <h3>Record Call</h3>
      </div>

      <div className="call-recorder-body">
        {/* Transcription Search Section */}
        {clientPhoneNumber && (
          <div className="transcription-search-section" style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
            <button
              type="button"
              onClick={() => setShowTranscriptionSearch(!showTranscriptionSearch)}
              style={{
                background: '#0066cc',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '100%',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {showTranscriptionSearch ? '▼ Hide' : '🔍 Search'} Call Transcriptions
            </button>

            {showTranscriptionSearch && (
              <div style={{ marginTop: '15px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <input
                    type="text"
                    value={searchPhoneNumber}
                    onChange={(e) => setSearchPhoneNumber(e.target.value)}
                    placeholder="Phone number (e.g., 07856496244 or +447856496244)"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        searchTranscriptions();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={searchTranscriptions}
                    disabled={searchLoading}
                    style={{
                      background: searchLoading ? '#ccc' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: '4px',
                      cursor: searchLoading ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>

                {searchError && (
                  <div style={{ padding: '10px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '10px' }}>
                    {searchError}
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                      Found {searchResults.length} call{searchResults.length !== 1 ? 's' : ''} in the last 30 days:
                    </p>
                    {searchResults.map((call, index) => (
                      <div
                        key={`${call.blob_name}-${index}`}
                        style={{
                          padding: '12px',
                          background: selectedCall?.blob_name === call.blob_name ? '#e3f2fd' : 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          marginBottom: '10px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '4px' }}>
                              {call.user.fullname} • {call.phone_number}
                              {call.extension && <span style={{ color: '#666' }}> • Ext: {call.extension}</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {call.call_datetime ? formatDateTime(call.call_datetime) : 'Unknown date'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleUseTranscription(call)}
                            disabled={transcribing}
                            style={{
                              background: transcribing ? '#ccc' : '#0066cc',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: transcribing ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {transcribing ? 'Transcribing...' : 'Use This'}
                          </button>
                        </div>

                        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                          <strong>File:</strong> {call.filename}
                        </div>

                        {call.transcription && (
                          <div style={{ fontSize: '12px', color: '#666', maxHeight: '100px', overflow: 'hidden', marginTop: '8px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                            <strong>Transcription preview:</strong> {call.transcription.substring(0, 200)}
                            {call.transcription.length > 200 && '...'}
                          </div>
                        )}

                        {!call.transcription && (
                          <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic', marginTop: '8px' }}>
                            Click "Use This" to transcribe this call
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Timer Display */}
        <div className="timer-section">
          <div className="timer-display">
            <span className="timer-value">
              {formatTime(elapsedTime)}
              {elapsedTime > 0 && (
                <span style={{ fontSize: '14px', marginLeft: '8px', color: '#666' }}>
                  ({Math.ceil(elapsedTime / 360)} unit{Math.ceil(elapsedTime / 360) !== 1 ? 's' : ''})
                </span>
              )}
            </span>
          </div>
          <div className="timer-controls">
            {!isRecording ? (
              <button
                className="timer-button start"
                onClick={handleStart}
                disabled={elapsedTime > 0}
              >
                {elapsedTime > 0 ? 'Call Ended' : 'Start Call'}
              </button>
            ) : (
              <button
                className="timer-button stop"
                onClick={handleStop}
              >
                End Call
              </button>
            )}
          </div>
        </div>

        {/* Call Direction */}
        <div className="form-group">
          <label>Call Direction</label>
          <div className="direction-toggle">
            <button
              type="button"
              className={`direction-btn ${direction === 'incoming' ? 'active' : ''}`}
              onClick={() => handleDirectionChange('incoming')}
            >
              📞 Incoming
            </button>
            <button
              type="button"
              className={`direction-btn ${direction === 'outgoing' ? 'active' : ''}`}
              onClick={() => handleDirectionChange('outgoing')}
            >
              📤 Outgoing
            </button>
          </div>
        </div>

        {/* From/To Fields */}
        <div className="form-row">
          <div className="form-group">
            <label>{direction === 'incoming' ? 'From (Caller)' : 'From (Agent)'}</label>
            <input
              type="text"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder={direction === 'incoming' ? 'Claimant name' : 'Your name'}
            />
          </div>
          <div className="form-group">
            <label>{direction === 'incoming' ? 'To (Agent)' : 'To (Recipient)'}</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={direction === 'incoming' ? 'Your name' : 'Claimant name'}
            />
          </div>
        </div>

        {/* Subject */}
        <div className="form-group">
          <label>Subject / Summary</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of the call"
          />
        </div>

        {/* Call Answered */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={callAnswered}
              onChange={(e) => setCallAnswered(e.target.checked)}
            />
            Call was answered
          </label>
        </div>

        {/* Call Notes */}
        <div className="form-group">
          <label>
            Call Notes
            {selectedCall && (
              <span style={{ marginLeft: '10px', fontSize: '12px', color: '#28a745' }}>
                ✓ Transcription attached from {selectedCall.call_datetime ? formatDateTime(selectedCall.call_datetime) : 'call recording'}
              </span>
            )}
          </label>
          <textarea
            value={callNotes}
            onChange={(e) => setCallNotes(e.target.value)}
            placeholder="Enter details about the call..."
            rows={6}
          />
        </div>
      </div>

      <div className="call-recorder-footer">
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={elapsedTime === 0}
        >
          Save Call Record
        </button>
      </div>
    </div>
  );
}
